import { useState, useEffect } from 'react';

/**
 * Hook pour charger les images protégées (base64 encodées)
 * Empêche le téléchargement direct en utilisant des data URIs
 * 
 * @param {string} imagePath - Chemin de l'image relative
 * @returns {object} { image: string, loading: boolean, error: string|null }
 */
const envBaseUrl = process.env.REACT_APP_API_BASE_URL;
const hostname = typeof window !== 'undefined' ? window.location?.hostname : '';
const isLocalHost = hostname === 'localhost' || hostname === '127.0.0.1';
const isEnvLocal =
  typeof envBaseUrl === 'string' &&
  (envBaseUrl.includes('localhost') || envBaseUrl.includes('127.0.0.1'));

const FALLBACK_API_BASE_URL = isLocalHost
  ? 'http://127.0.0.1:8000/api'
  : 'https://api.krayneagency.fr/api';

const API_BASE_URL = envBaseUrl && (isLocalHost || !isEnvLocal)
  ? envBaseUrl
  : FALLBACK_API_BASE_URL;

const API_ROOT = API_BASE_URL.replace(/\/api\/?$/, '/');

const normalizeImagePath = (path) => {
  if (!path) return null;
  let cleanPath = String(path);
  if (cleanPath.startsWith('http')) {
    cleanPath = cleanPath.replace(/^https?:\/\/[^/]+\//, '');
  }
  cleanPath = cleanPath.replace(/^\/+/, '');
  if (cleanPath.startsWith('storage/')) {
    cleanPath = cleanPath.substring(8);
  }
  if (cleanPath.startsWith('api/images/')) {
    cleanPath = cleanPath.substring(11);
  }
  return cleanPath;
};

export const useProtectedImage = (imagePath) => {
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!imagePath) {
      setLoading(false);
      return;
    }

    const fetchImage = async () => {
      try {
        setLoading(true);
        const cleanPath = normalizeImagePath(imagePath);
        const token = localStorage.getItem('access_token');
        
        // Préparer les headers
        const headers = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        const response = await fetch(`${API_ROOT}api/images/${cleanPath}`, {
          headers,
          credentials: 'omit',
          mode: 'cors'
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        // Convertir en base64 data URI au lieu de blob URL
        // Cela empêche la réutilisation de l'URL dans d'autres onglets
        const blob = await response.blob();
        const reader = new FileReader();
        
        reader.onloadend = () => {
          setImage(reader.result); // data:image/jpeg;base64,...
          setError(null);
        };
        
        reader.onerror = () => {
          throw new Error('Erreur lecture image');
        };
        
        reader.readAsDataURL(blob);
        
      } catch (err) {
        console.error('Erreur lors du chargement de l\'image protégée:', err);
        setError(err.message);
        setImage(null);
      } finally {
        setLoading(false);
      }
    };

    fetchImage();

    // Plus besoin de cleanup car on n'utilise plus createObjectURL
  }, [imagePath]);

  return { image, loading, error };
};
